import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = { class: "slider-container" };
const _hoisted_2 = {
    key: 0,
    class: "background-ranges"
};
const _hoisted_3 = ["title"];
const _hoisted_4 = ["min", "max", "step"];
const _hoisted_5 = ["min", "max", "step"];
const _hoisted_6 = { class: "slider-labels" };
const _hoisted_7 = { class: "flex justify-between text-xs text-gray-600 mt-2" };
const _hoisted_8 = {
    key: 0,
    class: "current-values mt-2"
};
const _hoisted_9 = { class: "flex items-center justify-between" };
const _hoisted_10 = { class: "flex items-center justify-between" };
import { ref, computed, watch } from 'vue';
import { useTheme } from '../../composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'DualRangeSlider',
    props: {
        min: {},
        max: {},
        absoluteMin: {},
        absoluteMax: {},
        showMinMax: { type: Boolean },
        step: { default: 1 },
        color: { default: 'red' },
        label: {},
        formatValue: {},
        backgroundRanges: {},
        backgroundStyle: { default: 'dashed' }
    },
    emits: ["update:min", "update:max"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { theme } = useTheme();
        // Local state
        const localMin = ref(props.min);
        const localMax = ref(props.max);
        const lastChanged = ref('min');
        const isDragging = ref(false);
        const dragStartX = ref(0);
        const dragStartValue = ref(0);
        const withOpacity = (hexColor, opacity) => {
            const hex = (hexColor || '').replace('#', '');
            if (!/^[0-9a-fA-F]{6}$/.test(hex))
                return hexColor;
            const alpha = Math.round(Math.min(1, Math.max(0, opacity)) * 255).toString(16).padStart(2, '0');
            return `#${hex}${alpha}`;
        };
        const sliderThemeVars = computed(() => {
            const primary = theme.value.colors.primary || '#2563eb';
            const warning = theme.value.colors.warning || primary;
            return {
                '--slider-primary': primary,
                '--slider-primary-15': withOpacity(primary, 0.15),
                '--slider-primary-20': withOpacity(primary, 0.2),
                '--slider-primary-25': withOpacity(primary, 0.25),
                '--slider-primary-30': withOpacity(primary, 0.3),
                '--slider-primary-70': withOpacity(primary, 0.7),
                '--slider-warning': warning
            };
        });
        // Computed
        const leftPercent = computed(() => {
            return ((localMin.value - props.absoluteMin) / (props.absoluteMax - props.absoluteMin)) * 100;
        });
        const widthPercent = computed(() => {
            return ((localMax.value - localMin.value) / (props.absoluteMax - props.absoluteMin)) * 100;
        });
        // Watch for prop changes
        watch(() => props.min, (newValue) => {
            localMin.value = newValue;
        });
        watch(() => props.max, (newValue) => {
            localMax.value = newValue;
        });
        // Methods
        const startDrag = (type, event) => {
            lastChanged.value = type;
            isDragging.value = true;
            const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
            dragStartX.value = clientX;
            dragStartValue.value = type === 'min' ? localMin.value : localMax.value;
        };
        const isTimeInOperatingHours = (minutes) => {
            if (!props.backgroundRanges?.length)
                return true;
            return props.backgroundRanges.some(range => minutes >= range.start && minutes <= range.end);
        };
        const findNearestValidTime = (minutes) => {
            if (!props.backgroundRanges?.length)
                return minutes;
            let closestValid = minutes;
            let minDistance = Infinity;
            for (const range of props.backgroundRanges) {
                // Check if we can snap to start of range
                const distanceToStart = Math.abs(minutes - range.start);
                if (distanceToStart < minDistance) {
                    minDistance = distanceToStart;
                    closestValid = range.start;
                }
                // Check if we can snap to end of range
                const distanceToEnd = Math.abs(minutes - range.end);
                if (distanceToEnd < minDistance) {
                    minDistance = distanceToEnd;
                    closestValid = range.end;
                }
                // If within range, keep original value
                if (minutes >= range.start && minutes <= range.end) {
                    return minutes;
                }
            }
            return closestValid;
        };
        const handleMinChange = () => {
            // Constraint zu Operating Hours wenn Background Ranges vorhanden
            if (props.backgroundRanges?.length && !isTimeInOperatingHours(localMin.value)) {
                localMin.value = findNearestValidTime(localMin.value);
            }
            if (localMin.value > localMax.value) {
                localMin.value = localMax.value;
            }
            lastChanged.value = 'min';
            emit('update:min', localMin.value);
        };
        const handleMaxChange = () => {
            // Constraint zu Operating Hours wenn Background Ranges vorhanden
            if (props.backgroundRanges?.length && !isTimeInOperatingHours(localMax.value)) {
                localMax.value = findNearestValidTime(localMax.value);
            }
            if (localMax.value < localMin.value) {
                localMax.value = localMin.value;
            }
            lastChanged.value = 'max';
            emit('update:max', localMax.value);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "dual-range-slider",
                style: _normalizeStyle(sliderThemeVars.value)
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createCommentVNode(" Background Ranges (Öffnungszeiten) "),
                    (_ctx.backgroundRanges?.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.backgroundRanges, (range, index) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: index,
                                    class: _normalizeClass(["background-range", `background-${_ctx.backgroundStyle}`]),
                                    style: _normalizeStyle({
                                        left: `${((range.start - _ctx.absoluteMin) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`,
                                        width: `${((range.end - range.start) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`,
                                        zIndex: 10 - index
                                    }),
                                    title: range.label
                                }, null, 14 /* CLASS, STYLE, PROPS */, _hoisted_3));
                            }), 128 /* KEYED_FRAGMENT */))
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createCommentVNode(" Track "),
                    _cache[6] || (_cache[6] = _createElementVNode("div", { class: "slider-track" }, null, -1 /* CACHED */)),
                    _createCommentVNode(" Range Fill "),
                    _createElementVNode("div", {
                        class: _normalizeClass(["slider-range", `slider-range-${_ctx.color}`]),
                        style: _normalizeStyle({ left: `${leftPercent.value}%`, width: `${widthPercent.value}%` })
                    }, null, 6 /* CLASS, STYLE */),
                    _createCommentVNode(" Min Handle "),
                    _withDirectives(_createElementVNode("input", {
                        ref: "minInput",
                        type: "range",
                        min: _ctx.absoluteMin,
                        max: _ctx.absoluteMax,
                        step: _ctx.step,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((localMin).value = $event)),
                        class: _normalizeClass(["slider-input slider-input-min", `slider-input-${_ctx.color}`]),
                        style: _normalizeStyle({ zIndex: localMin.value === localMax.value ? (lastChanged.value === 'min' ? 3 : 1) : 2 }),
                        onInput: handleMinChange,
                        onMousedown: _cache[1] || (_cache[1] = ($event) => (startDrag('min', $event))),
                        onTouchstart: _cache[2] || (_cache[2] = ($event) => (startDrag('min', $event)))
                    }, null, 46 /* CLASS, STYLE, PROPS, NEED_HYDRATION */, _hoisted_4), [
                        [
                            _vModelText,
                            localMin.value,
                            void 0,
                            { number: true }
                        ]
                    ]),
                    _createCommentVNode(" Max Handle "),
                    _withDirectives(_createElementVNode("input", {
                        ref: "maxInput",
                        type: "range",
                        min: _ctx.absoluteMin,
                        max: _ctx.absoluteMax,
                        step: _ctx.step,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((localMax).value = $event)),
                        class: _normalizeClass(["slider-input slider-input-max", `slider-input-${_ctx.color}`]),
                        style: _normalizeStyle({ zIndex: localMin.value === localMax.value ? (lastChanged.value === 'max' ? 3 : 1) : 2 }),
                        onInput: handleMaxChange,
                        onMousedown: _cache[4] || (_cache[4] = ($event) => (startDrag('max', $event))),
                        onTouchstart: _cache[5] || (_cache[5] = ($event) => (startDrag('max', $event)))
                    }, null, 46 /* CLASS, STYLE, PROPS, NEED_HYDRATION */, _hoisted_5), [
                        [
                            _vModelText,
                            localMax.value,
                            void 0,
                            { number: true }
                        ]
                    ])
                ]),
                _createCommentVNode(" Value Labels "),
                _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("span", null, _toDisplayString(_ctx.formatValue ? _ctx.formatValue(_ctx.absoluteMin) : _ctx.absoluteMin), 1 /* TEXT */),
                        _createElementVNode("span", null, _toDisplayString(_ctx.formatValue ? _ctx.formatValue(_ctx.absoluteMax) : _ctx.absoluteMax), 1 /* TEXT */)
                    ])
                ]),
                _createCommentVNode(" Current Values Display "),
                (_ctx.showMinMax)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-sm text-gray-700" }, "Von:", -1 /* CACHED */)),
                            _createElementVNode("span", {
                                class: _normalizeClass(`text-${_ctx.color}-600 font-medium`)
                            }, _toDisplayString(_ctx.formatValue ? _ctx.formatValue(localMin.value) : localMin.value), 3 /* TEXT, CLASS */)
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-sm text-gray-700" }, "Bis:", -1 /* CACHED */)),
                            _createElementVNode("span", {
                                class: _normalizeClass(`text-${_ctx.color}-600 font-medium`)
                            }, _toDisplayString(_ctx.formatValue ? _ctx.formatValue(localMax.value) : localMax.value), 3 /* TEXT, CLASS */)
                        ])
                    ]))
                    : _createCommentVNode("v-if", true)
            ], 4 /* STYLE */));
        };
    }
});
